<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import { openModal } from 'svelte-modals';
    import { z } from 'zod';

    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import InputEmail from '../../shared/InputEmail.svelte';
    import ButtonSmallBlack from '../../shared/ButtonSmallBlack.svelte';
    import LinkSmallDanger from '../../shared/LinkSmallDanger.svelte';
    import MessageDangerSmall from '../../shared/Messages/MessageDangerSmall.svelte';
    import ModalConfirm from '../../shared/Modals/ModalConfirm.svelte';
    import { api } from '../../../stores/api';
    import type { RecipientType } from './StatsRecipientsApi';
    import { StatsRecipientsApp } from './StatsRecipientsApp';

    let recipientsApp: StatsRecipientsApp = new StatsRecipientsApp($api);

    let email: string = '';
    /** Wymusza czerwoną ramkę InputEmail także dla pustego pola - po nieudanej próbie dodania. */
    let markDirty: boolean = false;
    let isSubmitting: boolean = false;

    /**
     * Klucz i18n błędu wykrytego jeszcze w przeglądarce (zły format adresu). Trzymany osobno od
     * stanu, bo StatsRecipientsApp raportuje wyłącznie odmowy backendu, a walidacja formatu nie
     * dochodzi do żądania.
     */
    let localError: string | null = null;

    /** Do widoku idzie JEDEN komunikat: świeższy błąd formularza wypiera poprzednią odmowę. */
    $: errorKey = localError ?? recipientsApp.getError();

    onMount(() => {
        recipientsApp.load().then(() => {
            recipientsApp = recipientsApp;
        });
    });

    /**
     * Svelte 4 nie śledzi mutacji wewnątrz klasy, więc po każdym `await` ręcznie przypisujemy
     * `recipientsApp = recipientsApp` (wzór UsageStats.svelte). Format adresu sprawdzamy tym samym
     * zod-em co InputEmail: bez tego pusty formularz poleciałby do backendu po odmowę email_invalid.
     */
    async function addRecipient(): Promise<void> {
        localError = null;

        if (!z.string().email().safeParse(email.trim()).success) {
            markDirty = true;
            localError = 'Enter a valid e-mail address';

            return;
        }

        isSubmitting = true;
        const added = await recipientsApp.add(email);
        isSubmitting = false;
        recipientsApp = recipientsApp;

        if (added) {
            email = '';
            markDirty = false;
        }
    }

    async function removeRecipient(id: number): Promise<void> {
        localError = null;
        isSubmitting = true;
        await recipientsApp.remove(id);
        isSubmitting = false;
        recipientsApp = recipientsApp;
    }

    /**
     * Usunięcie idzie przez potwierdzenie - lista bywa jednoelementowa, a pusta oznacza, że raport
     * przestaje wychodzić do kogokolwiek. ModalConfirm tłumaczy `title` i `text` u siebie, więc
     * przekazany tekst przechodzi przez `$_()` drugi raz i wraca bez zmian (brak takiego klucza w
     * locale). Wołanie `$_()` tutaj zostaje, bo to ono trzyma literały w skanie LocaleKeys.test.ts.
     *
     * LinkSmallDanger nie przyjmuje `disabled`, więc trwające żądanie odcinamy tutaj - przygaszenie
     * linku samym CSS-em nie jest gwarancją, a dwa równoległe `remove()` rozjechałyby recipientsApp.
     */
    function confirmRemove(recipient: RecipientType): void {
        if (isSubmitting) {
            return;
        }

        openModal(ModalConfirm, {
            title: $_('Remove recipient'),
            text: $_('Are you sure you want to remove this recipient?'),
            onClick: () => {
                void removeRecipient(recipient.id);
            },
        });
    }
</script>

<div class="stats-recipients">
    <p class="legend">{$_('Recipients of the usage statistics e-mail')}</p>

    {#if recipientsApp.isLoading()}
        <div class="loading">
            <Spinner />
        </div>
    {:else}

        <div class="add-form">
            <!-- jawna ramka w `style`, bo motyw ustawia --input-small-border na 0px: bez niej pole
                 zlewa się z tłem, a czerwone podświetlenie InputEmail (dopisywane border-color)
                 nie miałoby czego pomalować -->
            <InputEmail bind:value={email} {markDirty} style="min-width: 260px; border: 1px solid var(--automapa-grey-dark);" />
            <!-- ButtonSmallBlack nie patrzy na can_write i tak ma tu być: lista odbiorców jest
                 operacją admina, a is_admin jest w e-tollu niezależne od can_write (patrz
                 Admin::addStatsRecipient - brak wpisu w PermissionGuard::WRITING_CUSTOM_OPERATIONS).
                 Bramka na can_write odcięłaby admina, któremu backend na tę operację pozwala. -->
            <ButtonSmallBlack text={$_('Add recipient')} disabled={isSubmitting} on:click={addRecipient} />
        </div>

        {#if errorKey}
            <div class="error">
                <MessageDangerSmall message={$_(errorKey)} />
            </div>
        {/if}

        {#if recipientsApp.isEmpty()}
            <!-- pusta lista to nie brak danych, tylko wyłączona wysyłka - Cron::sendCustomerStats()
                 zapisuje wtedy wpis do error_log i nie wysyła maila -->
            <p class="warning">{$_('No recipients - the statistics e-mail will not be sent')}</p>
        {:else}
            <table class="table-recipients">
                <thead>
                    <tr>
                        <th>{$_('E-mail address')}</th>
                        <th>{$_('Added')}</th>
                        <th class="cell-actions"></th>
                    </tr>
                </thead>
                <tbody>
                    {#each recipientsApp.getRecipients() as recipient (recipient.id)}
                        <tr>
                            <td>{recipient.email}</td>
                            <td class="cell-date">{recipient.created_at}</td>
                            <td class="cell-actions">
                                <!-- tooltip siedzi na obudowie, bo LinkSmallDanger nie przyjmuje title -->
                                <span class="action" class:busy={isSubmitting} title={$_('Remove recipient')}>
                                    <LinkSmallDanger text={$_('Remove')} on:click={() => confirmRemove(recipient)} />
                                </span>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/if}

    {/if}
</div>

<style>
    .stats-recipients {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
        align-items: flex-start;
    }
    .legend {
        margin: 0;
        font-size: 0.85rem;
        opacity: 0.8;
    }
    .loading {
        display: flex;
        align-items: center;
        column-gap: 20px;
    }
    .warning {
        margin: 0;
        color: var(--automapa-red);
    }
    .table-recipients {
        border-collapse: collapse;
    }
    .table-recipients th {
        padding: 0.5rem;
        border: 1px solid #ddd;
        text-align: left;
        font-weight: 200;
        opacity: 0.8;
    }
    .table-recipients td {
        padding: 0.5rem;
        border: 1px solid #ddd;
        text-align: left;
    }
    .table-recipients td.cell-date {
        white-space: nowrap;
    }
    .table-recipients .cell-actions {
        border: none;
        padding-left: 12px;
    }
    .table-recipients .action.busy {
        opacity: 0.4;
        pointer-events: none;
    }
    .add-form {
        display: flex;
        align-items: center;
        /* 6px, bo ButtonSmallBlack dokłada własne `margin: 0 6px`, którego scoped-CSS tego
           komponentu nie dosięgnie */
        column-gap: 6px;
    }
</style>
